import React, { useState, useEffect } from 'react'
import { format, addDays, isToday, isTomorrow, isPast, differenceInMinutes, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfDay } from 'date-fns'
import { CalendarDays, Camera } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/helpers'
import { getCurrentTimeInTimezone, toConfiguredTimezone, getTodayDateString, getTomorrowDateString, extractDateInTimezoneSync, getConfiguredTimezone, parseDueDate } from '@/utils/timezoneUtils'
import {
  AlertCircle, Clock, Trophy, Star, CheckCircle,
  ChevronRight, Bus, Car, School, Calendar,
  Zap, Target, AlertTriangle, Sparkles, History, ChevronDown,
  Eye, X, UtensilsCrossed, Flame
} from 'lucide-react'
import { APP_VERSION } from '@/version'
import WhereAmI from './kids/WhereAmI'
import { SchoolCalendarView } from './SchoolCalendarView'
import { PWAInstallPrompt, PWAInstallButton } from './PWAInstallPrompt'
import PhotoUploadModal from './PhotoUploadModal'
import { getIconDisplay } from '@/utils/iconMapping'

interface Assignment {
  id: string
  chore_id: string
  assigned_to?: string | null
  status: string
  due_date: string
  points_earned?: number
  is_bonus?: boolean
  bonus_multiplier?: number
  completed_at?: string
  verified_at?: string
  chore: {
    name: string
    description?: string
    base_points: number
    estimated_minutes: number
    icon?: string
    category?: string
    requires_photo?: boolean
    requires_verification?: boolean
  }
}

interface TransportInfo {
  mode: 'bus' | 'car' | 'drives_self' | 'no_school' | 'digital_learning' | null
  departure_time?: string
  bus_number?: string
  at_toms_house: boolean
  isToday?: boolean
  notes?: string[]
  school_events?: Array<{type: string, description: string}>
}

interface DailyStats {
  completed_today: number
  points_today: number
  daily_goal: number
  streak_days: number
}

export function KidsDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [todayTasks, setTodayTasks] = useState<Assignment[]>([])
  const [tomorrowTasks, setTomorrowTasks] = useState<Assignment[]>([])
  const [overdueTasks, setOverdueTasks] = useState<Assignment[]>([])
  const [bonusTasks, setBonusTasks] = useState<Assignment[]>([])
  const [historyTasks, setHistoryTasks] = useState<Assignment[]>([])
  const [transportInfo, setTransportInfo] = useState<TransportInfo | null>(null)
  const [dailyStats, setDailyStats] = useState<DailyStats | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [photoModalTask, setPhotoModalTask] = useState<Assignment | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [activeTab, setActiveTab] = useState<'today' | 'tomorrow' | 'bonus' | 'history'>('today')
  const [usingMockStats, setUsingMockStats] = useState(false)
  const [showBreakdownModal, setShowBreakdownModal] = useState(false)
  const [pointsBreakdown, setPointsBreakdown] = useState<any>(null)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [historyFilter, setHistoryFilter] = useState<'week' | 'month' | 'all'>('week')
  const [showHistoryOptions, setShowHistoryOptions] = useState(false)
  const [todayInTimezone, setTodayInTimezone] = useState<Date>(new Date())
  const [schoolEvents, setSchoolEvents] = useState<any[]>([])
  const [userSchool, setUserSchool] = useState<string | null>(null)
  const [showSchoolCalendar, setShowSchoolCalendar] = useState(false)
  const [tonightsDinner, setTonightsDinner] = useState<{ name: string; description?: string } | null>(null)

  useEffect(() => {
    if (user) {
      // Always load dashboard data when user is available
      initializeTimezoneAndLoadData()
      
      // Also check if we came from Google OAuth login
      const params = new URLSearchParams(window.location.search)
      if (params.get('google_login') === 'true') {
        // Force a re-load after a brief delay to ensure all data is fresh
        setTimeout(() => {
          initializeTimezoneAndLoadData()
        }, 200)
      }
    } else {
      // No user means still authenticating or not logged in
      // Set loading to false after a brief delay to prevent infinite loading
      const timer = setTimeout(() => {
        setLoading(false)
      }, 500)
      return () => clearTimeout(timer)
    }
    // Check for bonus notification parameter
    const params = new URLSearchParams(window.location.search)
    const bonusTaskId = params.get('bonusTask')
    if (bonusTaskId) {
      setActiveTab('bonus')
      setSelectedTaskId(bonusTaskId)
      // Clear the parameter
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [user])

  const fetchPointsBreakdown = async () => {
    if (!user) return
    setBreakdownLoading(true)
    try {
      const response = await apiClient.get(`/api/points/breakdown/${user.username}?period=month`)
      setPointsBreakdown(response)
      setShowBreakdownModal(true)
    } catch (error) {
      console.error('Error fetching points breakdown:', error)
      toast.error('Failed to load points breakdown')
    } finally {
      setBreakdownLoading(false)
    }
  }

  const initializeTimezoneAndLoadData = async () => {
    try {
      const today = await getCurrentTimeInTimezone()
      setTodayInTimezone(startOfDay(today))
      await loadDashboardData(startOfDay(today))
    } catch (error) {
      console.error('Error initializing timezone:', error)
      // Fallback to browser timezone
      const today = startOfDay(new Date())
      setTodayInTimezone(today)
      await loadDashboardData(today)
    }
  }

  const loadDashboardData = async (todayDate?: Date) => {
    setLoading(true)
    try {
      // Load assignments
      const assignments = await apiClient.getMyAssignments()

      const today = todayDate || todayInTimezone
      const tomorrow = addDays(today, 1)

      // Get today and tomorrow as strings in the configured timezone
      const todayStr = await getTodayDateString()
      const tomorrowStr = await getTomorrowDateString()

      // Categorize tasks
      const todayList: Assignment[] = []
      const tomorrowList: Assignment[] = []
      const overdueList: Assignment[] = []
      const bonusList: Assignment[] = []

      // Defensive check: Ensure assignments is an array
      const assignmentsArray = Array.isArray(assignments) ? assignments :
                               (assignments?.assignments && Array.isArray(assignments.assignments)) ? assignments.assignments : []

      assignmentsArray.forEach((task: Assignment) => {
        // Get the date part from the task's due_date using family timezone
        const taskDateStr = extractDateInTimezoneSync(task.due_date)

        // Skip completed/verified tasks
        if (task.status === 'completed' || task.status === 'verified') {
          return
        }

        // Include bonus tasks OR open tasks (unassigned)
        if (task.is_bonus || !task.assigned_to || task.status === 'open') {
          // Include all open/bonus tasks (even overdue ones stay available)
          bonusList.push(task)
        } else {
          // Regular tasks - use string comparison in configured timezone
          if (taskDateStr < todayStr) {
            overdueList.push(task)
          } else if (taskDateStr === todayStr) {
            todayList.push(task)
          } else if (taskDateStr === tomorrowStr) {
            tomorrowList.push(task)
          }
        }
      })
      
      setTodayTasks(todayList)
      setTomorrowTasks(tomorrowList)
      setOverdueTasks(overdueList)
      setBonusTasks(bonusList)
      
      // Load transport info for the next school day
      const todayDay = today.getDay()
      const tomorrowDay = tomorrow.getDay()
      let schoolDate = null
      let isToday = false
      
      // Check if today is a school day and not past departure time
      if (todayDay >= 1 && todayDay <= 5) {
        const now = todayDate || todayInTimezone
        const departureHour = 8
        const departureMinute = 45 // Latest departure time
        if (now.getHours() < departureHour || (now.getHours() === departureHour && now.getMinutes() < departureMinute)) {
          schoolDate = today
          isToday = true
        }
      }
      
      // If not today, check tomorrow
      if (!schoolDate && tomorrowDay >= 1 && tomorrowDay <= 5) {
        schoolDate = tomorrow
        isToday = false
      }
      
      // Transport info temporarily disabled - router not enabled
      // TODO: Re-enable when backend router is activated
      /*
      if (schoolDate) {
        try {
          const transport = await apiClient.get(...)
          // Transport loading code
        } catch (error) {
          console.error('Failed to load transport info:', error)
        }
      }
      */

      // Load school events - use backend filtering by user_id
      try {
        // Pass user_id to backend for proper SQL filtering
        // Include days_back=7 to show recent past and future events only
        const params = user?.id ? { user_id: user.id, days_back: 7 } : { days_back: 7 }
        const schoolEventsData = await apiClient.get('/api/schools/events', { params })
        const eventsArray = Array.isArray(schoolEventsData) ? schoolEventsData : []

        // Set school events - already filtered by backend
        setSchoolEvents(eventsArray)

        // Get user's school name for display purposes
        if (user?.id) {
          try {
            const userInfo = await apiClient.get(`/api/users/${user.id}`)
            if (userInfo?.school) {
              setUserSchool(userInfo.school)
            } else {
            }
          } catch (userError) {
            console.error('[KidsDashboard] Failed to load user school info:', userError)
          }
        }
      } catch (error) {
        console.error('[KidsDashboard] Failed to load school events:', error)
        setSchoolEvents([])
      }

      // Load tonight's dinner
      // API returns array of DayPlan objects with nested breakfast/lunch/dinner
      try {
        const response = await apiClient.get(`/api/meals/week-plan?start=${todayStr}&end=${todayStr}`)
        if (response && response.length > 0) {
          // Get today's plan and extract dinner
          const todayPlan = response.find((p: any) => p.date === todayStr)
          if (todayPlan?.dinner?.meal_name) {
            setTonightsDinner({
              name: todayPlan.dinner.meal_name,
              description: todayPlan.dinner.notes
            })
          } else {
            setTonightsDinner(null)
          }
        } else {
          setTonightsDinner(null)
        }
      } catch (error) {
        console.error('[KidsDashboard] Failed to load dinner:', error)
        setTonightsDinner(null)
      }

      // Try to load real daily stats first
      try {
        const statsResponse = await apiClient.get(`/api/kids/daily-stats/${user?.id}`)
        if (statsResponse && statsResponse.points_today !== undefined) {
          setDailyStats(statsResponse)
          setUsingMockStats(false)
        } else {
          throw new Error('No stats data')
        }
      } catch (error) {
        // Fall back to calculated mock stats
        console.log('Using calculated daily stats')
        const completedToday = todayTasks.filter(t => t.status === 'verified')
        setDailyStats({
          points_today: completedToday.reduce((sum, t) => sum + (t.chore?.points || 0), 0),
          tasks_completed: completedToday.length,
          streak_days: 0,
          daily_goal: 3
        })
        setUsingMockStats(true)
      }
      
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      toast.error('Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }

  const loadHistoryData = async () => {
    setLoadingHistory(true)
    try {
      // Calculate date range based on filter
      let dateFrom: Date
      let dateTo = todayInTimezone
      
      switch (historyFilter) {
        case 'week':
          dateFrom = subDays(todayInTimezone, 7)
          break
        case 'month':
          dateFrom = subDays(todayInTimezone, 30)
          break
        case 'all':
          dateFrom = subDays(todayInTimezone, 365) // Last year
          break
      }
      
      // Get completed/verified assignments
      if (!user?.id) {
        console.warn('User not available for history lookup')
        return
      }
      
      const response = await apiClient.get(
        `/api/assignments?status=completed&user_id=${user.id}&start_date=${format(dateFrom, 'yyyy-MM-dd')}&end_date=${format(dateTo, 'yyyy-MM-dd')}`
      )

      // Also get verified assignments
      const verifiedResponse = await apiClient.get(
        `/api/assignments?status=verified&user_id=${user.id}&start_date=${format(dateFrom, 'yyyy-MM-dd')}&end_date=${format(dateTo, 'yyyy-MM-dd')}`
      )

      // Extract assignments arrays - handle both direct arrays and {assignments: [...]} format
      const completedArray = Array.isArray(response) ? response : (response?.assignments || [])
      const verifiedArray = Array.isArray(verifiedResponse) ? verifiedResponse : (verifiedResponse?.assignments || [])

      // Combine and sort by completion date
      const allHistory = [...completedArray, ...verifiedArray]
        .sort((a, b) => {
          const dateA = new Date(a.completed_at || a.verified_at || a.due_date)
          const dateB = new Date(b.completed_at || b.verified_at || b.due_date)
          return dateB.getTime() - dateA.getTime()
        })
      
      setHistoryTasks(allHistory)
    } catch (error) {
      console.error('Failed to load history:', error)
      toast.error('Failed to load history')
    } finally {
      setLoadingHistory(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'history' && historyTasks.length === 0) {
      loadHistoryData()
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistoryData()
    }
  }, [historyFilter])

  const handleTaskClick = (taskId: string) => {
    setSelectedTaskId(taskId === selectedTaskId ? null : taskId)
  }

  const handleCompleteTask = async (taskId: string, task?: Assignment) => {
    // If task requires photo, show the photo modal first
    if (task?.chore.requires_photo) {
      setPhotoModalTask(task)
      return
    }

    // Otherwise complete directly
    await submitTaskCompletion(taskId)
  }

  const submitTaskCompletion = async (taskId: string, photoFile?: File) => {
    try {
      console.log('Submitting task for verification:', { taskId, user, hasPhoto: !!photoFile })

      // Upload photo first if provided
      if (photoFile) {
        await apiClient.uploadCompletionPhoto(taskId, photoFile)
      }

      // Complete the assignment
      await apiClient.completeAssignment(taskId)
      toast.success('Task submitted for verification!')
      loadDashboardData()
    } catch (error: any) {
      console.error('Failed to submit task:', error)
      toast.error(`Failed to submit task: ${getErrorMessage(error, 'please try again')}`)
      throw error
    }
  }

  const handlePhotoSubmit = async (file: File | null) => {
    if (!photoModalTask) return

    try {
      await submitTaskCompletion(photoModalTask.id, file || undefined)
      setPhotoModalTask(null)
    } catch (error) {
      // Error already handled in submitTaskCompletion
    }
  }

  const handleTradeTask = (taskId: string) => {
    // Navigate to dashboard with trading view and task ID
    // The DashboardPage handles the view switching
    navigate(`/dashboard?view=trading&taskId=${taskId}`)
  }

  const handleClaimTask = async (taskId: string) => {
    try {
      // Claim the open task
      await apiClient.claimAssignment(taskId)
      toast.success('Task claimed!')
      loadDashboardData()
    } catch (error) {
      toast.error('Failed to claim task')
    }
  }

  const handleBidOnTask = (taskId: string) => {
    // Navigate to dashboard with trading view and task ID for bidding
    // The TradingInterface component handles both trading and bidding
    navigate(`/dashboard?view=trading&taskId=${taskId}&tab=bid`)
  }

  const getTimeUntilDeparture = () => {
    if (!transportInfo?.departure_time) return null
    
    const [time, period] = transportInfo.departure_time.split(' ')
    const [hours, minutes] = time.split(':').map(Number)
    const adjustedHours = period === 'PM' && hours !== 12 ? hours + 12 : hours
    
    const departure = new Date(todayInTimezone)
    departure.setHours(adjustedHours, minutes, 0, 0)
    
    // If departure is tomorrow
    if (departure < todayInTimezone) {
      departure.setDate(departure.getDate() + 1)
    }
    
    const minutesUntil = differenceInMinutes(departure, todayInTimezone)
    if (minutesUntil < 0) return null
    
    const hoursUntil = Math.floor(minutesUntil / 60)
    const minsRemaining = minutesUntil % 60
    
    if (hoursUntil > 24) return null
    if (hoursUntil > 0) return `${hoursUntil}h ${minsRemaining}m`
    return `${minsRemaining}m`
  }

  const renderTask = (task: Assignment, isOverdue: boolean = false) => {
    const isExpanded = selectedTaskId === task.id
    const points = task.is_bonus 
      ? Math.round(task.chore.base_points * (task.bonus_multiplier || 1.5))
      : task.chore.base_points
    
    // Determine task status display
    const getStatusDisplay = () => {
      switch(task.status) {
        case 'pending_verification':
          return <span className="text-yellow-600 font-medium">Awaiting Verification</span>
        case 'in_progress':
          return <span className="text-blue-600 font-medium">In Progress</span>
        case 'open':
          return <span className="text-green-600 font-medium">Available</span>
        default:
          if (isOverdue) {
            return <span className="text-red-600 font-medium">Overdue!</span>
          }
          return <span className="text-gray-600">Not Started</span>
      }
    }
    
    return (
      <div
        key={task.id}
        className={`bg-white rounded-xl shadow-sm border transition-all ${
          isOverdue ? 'border-red-400 bg-red-50' : 
          task.is_bonus ? 'border-green-400 bg-green-50' : 
          task.status === 'pending_verification' ? 'border-yellow-300 bg-yellow-50' :
          'border-gray-200'
        }`}
      >
        <button
          onClick={() => handleTaskClick(task.id)}
          className="w-full p-3 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3 flex-1">
            <div className={`text-2xl ${isOverdue ? 'animate-pulse' : ''}`}>
              {getIconDisplay(task.chore.icon)}
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {task.chore.name}
                {task.is_bonus && (
                  <Sparkles className="w-4 h-4 inline ml-1 text-yellow-500" />
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-600 mt-0.5">
                <span className="flex items-center gap-1">
                  <Trophy className="w-3 h-3" />
                  {points} pts
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {task.chore.estimated_minutes}m
                </span>
                <span className="flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  {format(parseDueDate(task.due_date), 'MMM d')}
                </span>
                {getStatusDisplay()}
              </div>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
            isExpanded ? 'rotate-90' : ''
          }`} />
        </button>
        
        {isExpanded && (
          <div className="px-3 pb-3 border-t">
            {task.chore.description && (
              <p className="text-sm text-gray-600 mt-2 mb-3">
                {task.chore.description}
              </p>
            )}
            
            {task.status === 'pending_verification' ? (
              <div className="text-center py-2 text-yellow-600 font-medium">
                ⏳ Waiting for parent to verify
              </div>
            ) : (
              <div className="space-y-2">
                {/* Open/unassigned tasks - show claim or bid options */}
                {(task.is_bonus || task.status === 'open' || !task.assigned_to) ? (
                  <>
                    {/* If task is overdue or worth less than 30 points, allow direct claim */}
                    {(isOverdue || task.chore.base_points < 30) ? (
                      <button
                        onClick={() => handleClaimTask(task.id)}
                        className="w-full py-2 rounded-lg font-medium bg-green-500 text-white hover:bg-green-600 transition-colors"
                      >
                        Claim Task ({points} points)
                      </button>
                    ) : (
                      <button
                        onClick={() => handleBidOnTask(task.id)}
                        className="w-full py-2 rounded-lg font-medium bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                      >
                        Place Bid ({points} points)
                      </button>
                    )}
                  </>
                ) : (
                  /* Assigned tasks - show submit for verification */
                  <>
                    <button
                      onClick={() => handleCompleteTask(task.id, task)}
                      className="w-full py-2 rounded-lg font-medium bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                    >
                      {task.chore.requires_photo && <Camera className="w-4 h-4" />}
                      {task.chore.requires_photo ? 'Take Photo & Submit' : 'Submit for Verification'} ({points} points)
                    </button>
                    
                    <button
                      onClick={() => handleTradeTask(task.id)}
                      className="w-full py-2 rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      Trade with Sibling
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">Please log in to view your dashboard</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Go to Login
          </button>
        </div>
      </div>
    )
  }

  const timeUntilDeparture = getTimeUntilDeparture()

  return (
    <div className="max-w-lg mx-auto pb-20">
      {/* Mock Data Indicator */}
      {usingMockStats && (
        <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
          <div className="flex items-center text-sm">
            <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
            <span className="text-yellow-800">Using calculated daily stats</span>
          </div>
        </div>
      )}
      
      {/* Critical Alerts */}
      {(overdueTasks.length > 0 || (transportInfo && timeUntilDeparture)) && (
        <div className="mb-4 space-y-2">
          {overdueTasks.length > 0 && (
            <div className="bg-red-100 border border-red-400 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-red-900">
                  {overdueTasks.length} Overdue {overdueTasks.length === 1 ? 'Task' : 'Tasks'}!
                </div>
                <div className="text-xs text-red-700">
                  Complete these first to avoid losing points
                </div>
              </div>
            </div>
          )}
          
          {transportInfo && (
            <div className={`border rounded-lg p-3 ${
              transportInfo.mode === 'digital_learning' ? 'bg-purple-100 border-purple-400' :
              transportInfo.mode === 'no_school' ? 'bg-green-100 border-green-400' :
              'bg-blue-100 border-blue-400'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {transportInfo.mode === 'bus' ? (
                    <Bus className="w-5 h-5 text-blue-600" />
                  ) : transportInfo.mode === 'car' ? (
                    <Car className="w-5 h-5 text-blue-600" />
                  ) : transportInfo.mode === 'digital_learning' ? (
                    <School className="w-5 h-5 text-purple-600" />
                  ) : transportInfo.mode === 'no_school' ? (
                    <Calendar className="w-5 h-5 text-green-600" />
                  ) : (
                    <Car className="w-5 h-5 text-blue-600" />
                  )}
                  <div>
                    {transportInfo.mode === 'no_school' ? (
                      <div className="font-medium text-green-900">
                        {transportInfo.notes?.[0] || 'No School Today'}
                      </div>
                    ) : transportInfo.mode === 'digital_learning' ? (
                      <div className="font-medium text-purple-900">
                        {transportInfo.notes?.[0] || 'Digital Learning Day'}
                      </div>
                    ) : (
                      <>
                        <div className="font-medium text-blue-900">
                          School {transportInfo.isToday ? 'Today' : 'Tomorrow'}: 
                          {transportInfo.departure_time && (
                            <span className="font-bold text-blue-600 ml-1 text-base">
                              {transportInfo.departure_time}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-blue-700">
                          {transportInfo.mode === 'bus' ? (
                            <>🚌 Bus #{transportInfo.bus_number}</>
                          ) : (
                            <>🚗 Car pickup</>
                          )}
                          {transportInfo.at_toms_house ? " from home" : " from other parent"}
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {timeUntilDeparture && transportInfo.mode !== 'no_school' && transportInfo.mode !== 'digital_learning' && (
                  <div className="text-sm font-bold text-blue-900">
                    {timeUntilDeparture}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Custody schedule widget: kids only (the endpoint answers per kid), renders only when there is data */}
      {!user?.is_parent && <WhereAmI username={user?.username || ''} />}

      {/* Streak Progress Widget */}
      {dailyStats && (() => {
        const streak = dailyStats.streak_days || 0
        const milestones = [7, 14, 30, 60, 90, 180, 365]
        const nextMilestone = milestones.find(m => m > streak) || 365
        const prevMilestone = milestones.filter(m => m <= streak).pop() || 0
        const daysToMilestone = nextMilestone - streak
        const progressInRange = streak - prevMilestone
        const rangeSize = nextMilestone - prevMilestone
        const progressPercent = Math.min(100, (progressInRange / rangeSize) * 100)

        return (
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-3 mb-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5" />
                <div>
                  <div className="text-xs opacity-90">Current Streak</div>
                  <div className="font-bold text-lg">
                    {streak} {streak === 1 ? 'day' : 'days'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-xs opacity-90">Next Milestone</div>
                  <div className="font-bold">
                    {daysToMilestone} days to {nextMilestone}-day
                  </div>
                </div>
                <button
                  onClick={fetchPointsBreakdown}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-lg transition-colors"
                  title="View Points Breakdown"
                >
                  <Eye className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="mt-2 bg-white/20 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )
      })()}

      {/* Task Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-4">
        <div className="flex border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'today' 
                ? 'text-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Today
            {todayTasks.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">
                {todayTasks.length}
              </span>
            )}
            {activeTab === 'today' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('tomorrow')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'tomorrow' 
                ? 'text-blue-600 bg-blue-50' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Tomorrow
            {tomorrowTasks.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                {tomorrowTasks.length}
              </span>
            )}
            {activeTab === 'tomorrow' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('bonus')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'bonus' 
                ? 'text-green-600 bg-green-50' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center justify-center gap-1">
              Open
              <Zap className="w-4 h-4" />
            </span>
            {bonusTasks.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-600 rounded-full text-xs">
                {bonusTasks.length}
              </span>
            )}
            {activeTab === 'bonus' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600" />
            )}
          </button>
          
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors relative ${
              activeTab === 'history' 
                ? 'text-purple-600 bg-purple-50' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center justify-center gap-1">
              History
              <History className="w-4 h-4" />
            </span>
            {activeTab === 'history' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600" />
            )}
          </button>
        </div>
      </div>

      {/* Overdue Tasks (Always Visible) */}
      {overdueTasks.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            Overdue - Complete ASAP!
          </h3>
          <div className="space-y-2">
            {overdueTasks.map(task => renderTask(task, true))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="space-y-2">
        {activeTab === 'today' && (
          <>
            {todayTasks.length === 0 ? (
              <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                <div>
                  <div className="font-medium text-green-900">All done for today!</div>
                  <div className="text-sm text-green-700">Check out bonus tasks for extra points</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {todayTasks.map(task => renderTask(task))}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'tomorrow' && (
          <>
            {tomorrowTasks.length === 0 ? (
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-gray-400 flex-shrink-0" />
                <div>
                  <div className="font-medium text-gray-700">No tasks tomorrow</div>
                  <div className="text-sm text-gray-600">Enjoy your free day!</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-gray-600 mb-2">
                  Get ahead by preparing for tomorrow's tasks
                </div>
                {tomorrowTasks.map(task => renderTask(task))}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'bonus' && (
          <>
            {bonusTasks.length === 0 ? (
              <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
                <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                <div>
                  <div className="font-medium text-green-900">No open tasks available</div>
                  <div className="text-sm text-green-700">All tasks are assigned - check back later!</div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-green-700 mb-2 flex items-center gap-1">
                  <Sparkles className="w-4 h-4" />
                  Open tasks - claim these for extra points!
                </div>
                {bonusTasks.map(task => {
                  // Use timezone-aware date extraction for correct comparison
                  const taskDateStr = extractDateInTimezoneSync(task.due_date)
                  const today = todayInTimezone
                  const todayStr = format(today, 'yyyy-MM-dd')
                  const isOverdue = taskDateStr < todayStr
                  return renderTask(task, isOverdue)
                })}
              </div>
            )}
          </>
        )}
        
        {activeTab === 'history' && (
          <>
            {/* Date Filter Dropdown */}
            <div className="mb-4">
              <button
                onClick={() => setShowHistoryOptions(!showHistoryOptions)}
                className="w-full bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 flex items-center justify-between text-sm font-medium text-purple-700"
              >
                <span>
                  {historyFilter === 'week' && 'Last 7 Days'}
                  {historyFilter === 'month' && 'Last 30 Days'}
                  {historyFilter === 'all' && 'All Time'}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showHistoryOptions ? 'rotate-180' : ''}`} />
              </button>
              
              {showHistoryOptions && (
                <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                  <button
                    onClick={() => {
                      setHistoryFilter('week')
                      setShowHistoryOptions(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-purple-50 ${
                      historyFilter === 'week' ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    Last 7 Days
                  </button>
                  <button
                    onClick={() => {
                      setHistoryFilter('month')
                      setShowHistoryOptions(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-purple-50 border-t ${
                      historyFilter === 'month' ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    Last 30 Days
                  </button>
                  <button
                    onClick={() => {
                      setHistoryFilter('all')
                      setShowHistoryOptions(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-purple-50 border-t ${
                      historyFilter === 'all' ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    All Time
                  </button>
                </div>
              )}
            </div>
            
            {loadingHistory ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : historyTasks.length === 0 ? (
              <div className="bg-purple-50 rounded-xl p-6 text-center">
                <History className="w-12 h-12 text-purple-500 mx-auto mb-2" />
                <div className="font-medium text-purple-900">No completed tasks</div>
                <div className="text-sm text-purple-700 mt-1">
                  Your completed tasks will appear here
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-purple-700 mb-2">
                  {historyTasks.length} completed {historyTasks.length === 1 ? 'task' : 'tasks'}
                </div>
                {historyTasks.map(task => (
                  <div
                    key={task.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="text-2xl">
                          {getIconDisplay(task.chore.icon)}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {task.chore.name}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Trophy className="w-3 h-3" />
                              {task.points_earned || task.chore.base_points} pts earned
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {format(new Date(task.completed_at || task.verified_at || task.due_date), 'MMM d')}
                            </span>
                            {task.status === 'verified' ? (
                              <span className="text-green-600 font-medium flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Verified
                              </span>
                            ) : (
                              <span className="text-yellow-600 font-medium">Pending</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* School Events Widget */}
      {schoolEvents.length > 0 && (
        <div
          onClick={() => setShowSchoolCalendar(true)}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-4 cursor-pointer hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <School className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">
                Upcoming School Events
                {userSchool && <span className="text-sm text-gray-600 font-normal ml-1">at {userSchool}</span>}
              </h3>
            </div>
            <Calendar className="w-4 h-4 text-gray-400" />
          </div>
          <div className="space-y-2">
            {schoolEvents.filter((event: any) => {
              // Only show FUTURE events in this widget
              const eventDate = new Date(event.date + 'T12:00:00')
              const today = new Date(todayInTimezone)
              today.setHours(0, 0, 0, 0)
              return eventDate >= today
            }).slice(0, 5).map((event: any, index: number) => {
              const eventDate = new Date(event.date + 'T12:00:00')
              const todayDateStr = format(todayInTimezone, 'yyyy-MM-dd')
              const tomorrowDateStr = format(addDays(todayInTimezone, 1), 'yyyy-MM-dd')
              const eventDateStr = extractDateInTimezoneSync(event.date)

              const dayLabel = eventDateStr === todayDateStr ? 'Today' :
                             eventDateStr === tomorrowDateStr ? 'Tomorrow' :
                             format(eventDate, 'EEEE, MMM d')

              return (
                <div key={index} className="flex items-start gap-2 p-2 bg-purple-50 rounded border border-purple-100">
                  <Calendar className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {event.name}
                    </div>
                    <div className="text-xs text-purple-600 font-medium">
                      {dayLabel}
                    </div>
                    {event.description && (
                      <div className="text-xs text-gray-600 mt-1">
                        {event.description}
                      </div>
                    )}
                    {event.early_release && (
                      <div className="text-xs text-orange-600 font-medium mt-1">
                        Early Release: {event.early_release}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
            {schoolEvents.length > 5 && (
              <div className="text-xs text-center text-gray-500 pt-1">
                +{schoolEvents.length - 5} more events
              </div>
            )}
          </div>
        </div>
      )}

      {/* What's for Dinner Widget */}
      {tonightsDinner && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <UtensilsCrossed className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-gray-900">Tonight's Dinner</h3>
          </div>
          <div className="text-lg font-medium text-gray-800">
            {tonightsDinner.name}
          </div>
          {tonightsDinner.description && (
            <div className="text-sm text-gray-600 mt-1">
              {tonightsDinner.description}
            </div>
          )}
        </div>
      )}

      {/* Version Footer */}
      <div className="mt-8 text-center text-xs text-gray-400">
        v{APP_VERSION}
      </div>

      {/* Points Breakdown Modal */}
      {showBreakdownModal && pointsBreakdown && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            {/* Receipt Header */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">My Points</h2>
                  <p className="text-sm opacity-90">Points Statement</p>
                  <p className="text-xs opacity-75 mt-1">{pointsBreakdown.period}</p>
                </div>
                <button
                  onClick={() => {
                    setShowBreakdownModal(false)
                    setPointsBreakdown(null)
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Receipt Body */}
            <div className="p-6">
              {/* Chores Section */}
              {pointsBreakdown.breakdown.chores && pointsBreakdown.breakdown.chores.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">CHORES COMPLETED</h3>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {pointsBreakdown.breakdown.chores.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description.replace('Completed: ', '')}</span>
                        </div>
                        <span className="text-green-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-green-600 font-mono font-bold">+{pointsBreakdown.totals.earned}</span>
                  </div>
                </div>
              )}

              {/* Bonuses Section */}
              {pointsBreakdown.breakdown.bonuses && pointsBreakdown.breakdown.bonuses.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">BONUSES</h3>
                  <div className="space-y-1">
                    {pointsBreakdown.breakdown.bonuses.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-blue-600 font-mono">+{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-blue-600 font-mono font-bold">+{pointsBreakdown.totals.bonuses}</span>
                  </div>
                </div>
              )}

              {/* Deductions Section */}
              {pointsBreakdown.breakdown.deductions && pointsBreakdown.breakdown.deductions.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 mb-3">DEDUCTIONS</h3>
                  <div className="space-y-1">
                    {pointsBreakdown.breakdown.deductions.map((item: any, idx: number) => (
                      <div key={idx} className="flex justify-between items-center py-1 text-sm">
                        <div className="flex-1">
                          <span className="text-gray-600">{item.date}</span>
                          <span className="text-gray-800 ml-3">{item.description}</span>
                        </div>
                        <span className="text-red-600 font-mono">{item.points}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-2 mt-2 border-t">
                    <span className="text-gray-500 text-sm">Subtotal</span>
                    <span className="text-red-600 font-mono font-bold">{pointsBreakdown.totals.deductions}</span>
                  </div>
                </div>
              )}

              {/* Final Calculations */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xs font-semibold text-gray-500 mb-4">YOUR TOTALS</h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Earned from Chores</span>
                    <span className="font-mono">+{pointsBreakdown.totals.earned}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Bonuses</span>
                    <span className="font-mono">+{pointsBreakdown.totals.bonuses}</span>
                  </div>
                  {pointsBreakdown.totals.deductions !== 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Deductions</span>
                      <span className="font-mono">{pointsBreakdown.totals.deductions}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                    <span>LEADERBOARD POINTS</span>
                    <span className="font-mono text-blue-600 text-lg">{pointsBreakdown.totals.leaderboard_total}</span>
                  </div>
                </div>

                {/* Available Balance */}
                {pointsBreakdown.totals.spent > 0 && (
                  <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Rewards Used</span>
                      <span className="font-mono">-{pointsBreakdown.totals.spent}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 mt-2 border-t text-gray-900 font-semibold">
                      <span>POINTS TO SPEND</span>
                      <span className="font-mono text-green-600 text-lg">{pointsBreakdown.totals.available_balance}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* School Calendar Modal */}
      {showSchoolCalendar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto relative">
            <button
              onClick={() => setShowSchoolCalendar(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <div className="p-6">
              <SchoolCalendarView />
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={!!photoModalTask}
        onClose={() => setPhotoModalTask(null)}
        onSubmit={handlePhotoSubmit}
        choreName={photoModalTask?.chore.name || ''}
        isRequired={photoModalTask?.chore.requires_photo || false}
      />
    </div>
  )
}